import { ChangeDetectionStrategy, Component, computed, effect, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormControlStatus, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, startWith } from 'rxjs/operators';
import { FormStatus, HighAlertTaskFormValue } from '../task.dialog.model';
import { HighAlert, HighAlertWindow } from '../../../../models/high-alert.model';
import { TaskFormDialogBase } from '../task.form-base.dialog';
import { AppDropdownComponent, AppDropdownOption } from '../../../../shared/components/app-dropdown/app-dropdown.component';

const startBeforeEndValidator: ValidatorFn = (ctrl: AbstractControl): ValidationErrors | null => {
  const start = ctrl.get('startDate')?.value as string | null;
  const end = ctrl.get('endDate')?.value as string | null;

  // Let required validators handle empty
  if (!start || !end) return null;

  // Works for "YYYY-MM-DD" and "YYYY-MM-DDTHH:mm" lexicographically
  return start < end ? null : { startAfterEnd: true };
};

@Component({
  standalone: true,
  selector: 'dr-ops-stream-ui-highalert-task-dialog',
  imports: [CommonModule, ReactiveFormsModule, AppDropdownComponent],
  templateUrl: './highalert-task.dialog.html',
  styleUrls: ['./../task.dialog.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HighAlertTaskDialog extends TaskFormDialogBase<HighAlert, HighAlertTaskFormValue> {
  readonly windowTypes = signal<string[]>(Object.values(HighAlertWindow));

  readonly windowOptions = computed<AppDropdownOption[]>(() =>
    this.windowTypes().map((value, index) => ({
      value: value,
      label: value,
    }))
  );

  // -----------------------
  // Form
  // -----------------------
  readonly form = this.fb.group(
    {
      programId: this.fb.control<string>('', {
        nonNullable: true,
      }),

      programIds: this.fb.control<string[]>([], {
        nonNullable: true,
      }),
      notes: this.fb.control<string>('', { nonNullable: true }),
      startDate: this.fb.control<string | null>(null, { validators: [Validators.required] }),
      endDate: this.fb.control<string | null>(null, { validators: [Validators.required] }),
      timezone: this.fb.control<string>('', { nonNullable: true, validators: [Validators.required] }),
      window: this.fb.control<string>('', { nonNullable: true, validators: [Validators.required] }),
    },
    { validators: [startBeforeEndValidator] }
  );

  readonly formValid = toSignal(
    this.form.statusChanges.pipe(
      startWith(this.form.status),
      map((s: FormControlStatus) => s === FormStatus.VALID)
    ),
    { initialValue: this.form.valid }
  );

  readonly canSubmit = computed(() => !this.isViewOnly() && this.formValid());
  readonly confirmLabel = computed(() => (this.isEdit() ? 'Save' : 'Confirm'));

  readonly startEndInvalid = toSignal(
    this.form.statusChanges.pipe(
      startWith(this.form.status),
      map(() => {
        const s = this.form.controls.startDate.value;
        const e = this.form.controls.endDate.value;
        return !!s && !!e && !!this.form.errors?.['startAfterEnd'];
      })
    ),
    { initialValue: false }
  );

  constructor() {
    super();
    effect(() => {
      const defaults = (this.data.defaults ?? {}) as Partial<HighAlertTaskFormValue>;

      const defaultProgramId = defaults.programId ?? '';
      const defaultProgramIds = defaults.programIds ?? [];

      const baseInitial = {
        notes: defaults.notes ?? '',
        startDate: defaults.startDate ?? '',
        endDate: defaults.endDate ?? '',
        timezone: defaults.timezone ?? 'UTC',
        window: defaults.window ?? '',
      };

      const initial: HighAlertTaskFormValue = this.isCreate()
        ? {
            ...baseInitial,

            programId: '',
            programIds: defaultProgramIds,
          }
        : {
            ...baseInitial,

            programId: defaultProgramId,
            programIds: [],
          };

      this.form.reset(initial);

      this.updateProgramValidators();

      if (this.isViewOnly()) {
        this.form.disable({ emitEvent: false });
      } else {
        this.form.enable({ emitEvent: false });
      }
    });
  }

  private updateProgramValidators(): void {
    const programIdControl = this.form.controls.programId;
    const programIdsControl = this.form.controls.programIds;

    if (this.isCreate()) {
      programIdControl.clearValidators();
      programIdsControl.setValidators([Validators.required]);
    } else {
      programIdsControl.clearValidators();
      programIdControl.setValidators([Validators.required]);
    }

    programIdControl.updateValueAndValidity({ emitEvent: false });
    programIdsControl.updateValueAndValidity({ emitEvent: false });
  }
}
